import { Movie } from '../../../types/prediction-engine';

const PENDING_PREDICTION_KEY = 'prediction-engine-pending-request';

/** A hand-off older than this is treated as abandoned rather than re-run. */
const PENDING_MAX_AGE_MS = 10 * 60 * 1000;

export type PendingPredictionRequest = {
  id: string;
  created_at: number;
  movies: Movie[];
  model_id: string;
  // is_export_allowed lives on the load rows, not on the response, so it has to
  // travel with the request to be merged back in by index.
  export_flags: boolean[];
};

export type PendingPredictionPayload = Omit<
  PendingPredictionRequest,
  'id' | 'created_at'
>;

const createId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export const clearPendingPrediction = (): void => {
  try {
    localStorage.removeItem(PENDING_PREDICTION_KEY);
  } catch {
    // Nothing to clear.
  }
};

/**
 * Hands a prediction request to the results tab, returning the id that tab must
 * quote to claim it. localStorage writes are synchronous, so this can run inside
 * a click handler immediately before window.open — the only moment a mobile
 * browser will allow the tab. Returns null if storage is full or unavailable,
 * so the caller can fall back to running the request itself.
 */
export const stashPendingPrediction = (
  payload: PendingPredictionPayload,
): string | null => {
  const id = createId();

  try {
    localStorage.setItem(
      PENDING_PREDICTION_KEY,
      JSON.stringify({ ...payload, id, created_at: Date.now() }),
    );
    return id;
  } catch {
    return null;
  }
};

const readPendingPrediction = (id: string): PendingPredictionRequest | null => {
  try {
    const raw = localStorage.getItem(PENDING_PREDICTION_KEY);
    if (!raw) return null;

    const request = JSON.parse(raw) as PendingPredictionRequest;

    // Only the tab this request was opened for may run it, and only while it is
    // still fresh. Without both checks a hand-off whose tab was closed before it
    // loaded would be claimed by an unrelated later visit to the results route
    // — /predict is also reached from the Grouped and Detailed routes — and
    // silently re-run, overwriting current results and spending a prediction.
    if (request?.id !== id) return null;
    if (Date.now() - (request.created_at ?? 0) > PENDING_MAX_AGE_MS)
      return null;

    return request;
  } catch {
    return null;
  }
};

export const hasPendingPrediction = (id: string | null): boolean =>
  Boolean(id) && readPendingPrediction(id as string) !== null;

/** Reads and removes the request, so it can only ever run once. */
export const takePendingPrediction = (
  id: string | null,
): PendingPredictionRequest | null => {
  if (!id) return null;

  const request = readPendingPrediction(id);

  if (request) {
    clearPendingPrediction();
  }

  return request;
};
